import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { timetableService, formatTime12Hour } from '../services/TimetableService';
import { classInfoService } from '../services/ClassInfoService';
import './ParentTimetableView.css';
import './ParentDetailPages.css';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

function ParentTimetableView({ children }) {
  const [selectedChildIndex, setSelectedChildIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [entries, setEntries] = useState([]);
  const [timetableStatus, setTimetableStatus] = useState('Draft');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teacherNames, setTeacherNames] = useState({});
  const [selectedEntry, setSelectedEntry] = useState(null);

  const selectedChild = children && children.length > 0
    ? children[Math.min(selectedChildIndex, children.length - 1)]
    : null;

  useEffect(() => {
    if (selectedChild?.classId) {
      loadTimetable(selectedChild.classId);
    } else {
      setEntries([]);
    }
  }, [selectedChild?.classId]);

  useEffect(() => {
    setSelectedChildIndex(0);
  }, [children?.length]);

  const loadTimetable = async (classId) => {
    setLoading(true);
    setError('');
    try {
      const timetable = await timetableService.getTimetable(classId);
      const allEntries = timetable.entries || [];
      setEntries(allEntries);
      setTimetableStatus(timetable.status || 'Draft');

      // Load teacher names for entries that have teacherId
      const names = {};
      const classInfo = await classInfoService.getClassInfo(classId).catch(() => null);

      for (const entry of allEntries) {
        let teacherId = entry.teacherId;
        // Fallback: resolve from class subjectTeacherAssignments
        if (!teacherId && classInfo?.subjectTeacherAssignments && entry.subjectName) {
          teacherId = classInfo.subjectTeacherAssignments[entry.subjectName]
            || classInfo.subjectTeacherAssignments[entry.subjectName?.toLowerCase()];
        }
        // Fallback: homeroom teacher
        if (!teacherId) {
          teacherId = classInfo?.teacherId;
        }
        if (teacherId && !names[teacherId]) {
          try {
            const userDoc = await getDoc(doc(db, 'users', teacherId));
            if (userDoc.exists()) {
              const ud = userDoc.data();
              names[teacherId] = `${ud.firstName || ''} ${ud.lastName || ''}`.trim() || ud.fullName || 'Teacher';
            }
          } catch (e) {
            // Permission errors are expected for some users; skip silently
          }
        }
      }
      setTeacherNames(names);
    } catch (err) {
      console.error('ParentTimetableView: error loading timetable', err);
      setError('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  const dayEntries = entries
    .filter(entry => entry.dayOfWeek === selectedDay)
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

  const isCurrentEntry = (entry) => {
    if (!entry.startTime || !entry.endTime) return false;
    const now = new Date();
    const currentDay = WEEKDAYS[now.getDay() - 1];
    if (currentDay !== selectedDay) return false;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = entry.startTime.split(':').map(Number);
    const [eh, em] = entry.endTime.split(':').map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    return currentMinutes >= startMin && currentMinutes < endMin;
  };

  const getEntryTypeClass = (entry) => {
    if (entry.entryType === 'lunch') return 'lunch';
    if (entry.entryType === 'break') return 'break';
    return 'subject';
  };

  const getEntryTypeLabel = (entry) => {
    if (entry.entryType === 'lunch') return '🍽️ Lunch';
    if (entry.entryType === 'break') return '☕ Break';
    return entry.subjectName || 'Free';
  };

  if (!children || children.length === 0) {
    return (
      <div className="timetable-empty">
        <div className="empty-icon">📅</div>
        <h3>No Children Linked</h3>
        <p>Add a child to see their timetable.</p>
      </div>
    );
  }

  // Full-screen timetable entry detail (replaces the cramped bottom-sheet modal)
  if (selectedEntry) {
    return (
      <div className="detail-page">
        <button className="detail-back-button" onClick={() => setSelectedEntry(null)}>
          ◄ Back to Timetable
        </button>
        <div className="detail-hero">
          <div className="detail-avatar">
            {selectedEntry.entryType === 'lunch' ? '🍽️' : selectedEntry.entryType === 'break' ? '☕' : '📚'}
          </div>
          <div className="detail-hero-info">
            <h2>{getEntryTypeLabel(selectedEntry)}</h2>
            <p className="detail-subtitle">
              {selectedEntry.dayOfWeek || selectedDay} · {formatTime12Hour(selectedEntry.startTime)} - {formatTime12Hour(selectedEntry.endTime)}
            </p>
            <div className="detail-badges">
              {selectedEntry.entryType && <span className="detail-badge">{selectedEntry.entryType}</span>}
              {selectedChild && <span className="detail-badge gray">{selectedChild.fullName}</span>}
            </div>
          </div>
        </div>
        <div className="detail-grid">
          <div className="detail-card">
            <h3>Session Details</h3>
            <div className="detail-field"><span className="field-label">Day</span><span className="field-value">{selectedEntry.dayOfWeek || selectedDay}</span></div>
            <div className="detail-field"><span className="field-label">Time</span><span className="field-value">{formatTime12Hour(selectedEntry.startTime)} - {formatTime12Hour(selectedEntry.endTime)}</span></div>
            {selectedEntry.subjectName && <div className="detail-field"><span className="field-label">Subject</span><span className="field-value">{selectedEntry.subjectName}</span></div>}
            {selectedEntry.room && <div className="detail-field"><span className="field-label">Room</span><span className="field-value">{selectedEntry.room}</span></div>}
            {selectedEntry.teacherName && <div className="detail-field"><span className="field-label">Teacher</span><span className="field-value">{selectedEntry.teacherName}</span></div>}
            {selectedEntry.entryType && <div className="detail-field"><span className="field-label">Type</span><span className="field-value">{selectedEntry.entryType.charAt(0).toUpperCase() + selectedEntry.entryType.slice(1)}</span></div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-timetable">
      <div className="timetable-header">
        <h2>Timetable</h2>
      </div>

      {children.length > 1 && (
        <div className="child-selector">
          {children.map((child, index) => (
            <button
              key={child.id || index}
              className={`child-chip ${selectedChildIndex === index ? 'active' : ''}`}
              onClick={() => setSelectedChildIndex(index)}
            >
              {child.firstName || child.fullName}
            </button>
          ))}
        </div>
      )}

      {selectedChild && (
        <div className="selected-child-info">
          <p><strong>{selectedChild.fullName}</strong></p>
          <p>Class: {selectedChild.className || 'Not assigned'}</p>
        </div>
      )}

      {!selectedChild?.classId || selectedChild.classId === 'not-assigned' ? (
        <div className="timetable-empty">
          <p>This child is not assigned to a class yet.</p>
        </div>
      ) : (
        <>
          <div className="day-selector">
            {WEEKDAYS.map(day => (
              <button
                key={day}
                className={`day-chip ${selectedDay === day ? 'active' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>

          <div className="timetable-day-content">
            <h3>{selectedDay}</h3>
            {timetableStatus !== 'Published' ? (
              <p className="no-entries">Timetable is not published yet. Please check back later.</p>
            ) : loading ? (
              <div className="timetable-loading">
                <div className="spinner"></div>
                <p>Loading timetable...</p>
              </div>
            ) : dayEntries.length === 0 ? (
              <p className="no-entries">No timetable entries for {selectedDay}.</p>
            ) : (
              <div className="entries-list">
                {dayEntries.map(entry => {
                  const isNow = isCurrentEntry(entry);
                  const typeClass = getEntryTypeClass(entry);
                  const teacherName = entry.teacherId ? (teacherNames[entry.teacherId] || null) : null;
                  return (
                    <div
                      key={entry.id}
                      className={`entry-card ${typeClass} ${isNow ? 'now' : ''}`}
                      onClick={() => setSelectedEntry({ ...entry, teacherName })}
                    >
                      <div className="entry-main">
                        <span className="entry-subject">{getEntryTypeLabel(entry)}</span>
                        <span className="entry-time">{formatTime12Hour(entry.startTime)} - {formatTime12Hour(entry.endTime)}</span>
                        {entry.room && <span className="entry-room">Room {entry.room}</span>}
                        {teacherName && <span className="entry-teacher">👨‍🏫 {teacherName}</span>}
                      </div>
                      {isNow && <span className="now-badge">NOW</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {error && <p className="timetable-error">{error}</p>}
          </div>
        </>
      )}

    </div>
  );
}

export default ParentTimetableView;
